import { makeAutoObservable, runInAction } from 'mobx'
import { type ReactNode } from 'react'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { EnumDropdownItemVariant, type IDropdownItem } from 'shared/ui'
import { formatPhoneNumberNational, logger } from 'shared/lib'
import { type IAlert } from 'shared/ui/Alert/types'
import {
  type ContactsStore,
  type IContactAll,
  type IContactIntegrationAll,
  type IContactPhone,
} from 'entities/Contacts'
import { type IParamsCreateContact } from 'entities/Contacts/api/types'
import { ContactChoosePhoneModalActions } from 'entities/Contacts/ui/ContactChoosePhoneModal/ui/ContactChoosePhoneModalActions'
import { ContactChoosePhoneModalContent } from 'entities/Contacts/ui/ContactChoosePhoneModal/ui/ContactChoosePhoneModalContent'
import { type IntegrationsStore } from 'entities/Integrations'

type IContactChoosePhoneModalOptions = {
  contact: IContactIntegrationAll
  callback: (item: IContactAll) => void
  onClose?: () => void
  params?: {
    forLink: boolean
    fromConversation: boolean
  }
}

type IContactChoosePhoneModalStoreConfig = {
  onChangeAlert: (item: IAlert | null) => void
}

export class ContactChoosePhoneModalStore {
  private _idModal = 'contact-choose-phone'
  private _options: IContactChoosePhoneModalOptions | null = null
  private _loading = false
  private _numbersMap: Map<string, IDropdownItem> = new Map()
  private _activeNumber = ''
  private _activeLabel = ''
  private _activeField = ''
  private _activeType = ''
  private _alert: IAlert | null = null
  private _config: IContactChoosePhoneModalStoreConfig | null = null

  constructor(
    private _contactsStore: ContactsStore,
    private _integrationsStore: IntegrationsStore
  ) {
    makeAutoObservable(this)
  }

  setConfig = (config: IContactChoosePhoneModalStoreConfig) => {
    this._config = config
  }

  initNumbers = (template: (item: IContactPhone) => ReactNode) => {
    if (!this._options) return []

    this._options.contact.phoneNumbers.forEach((item) => {
      this._numbersMap.set(item.type, {
        id: item.type,
        label: item.field,
        labelContent: template(item),
        activeValue: this._activeType === item.type,
        value: item.number,
        variant: EnumDropdownItemVariant.CheckedRight,
      })
    })
  }

  openModal = (options: IContactChoosePhoneModalOptions) => {
    this._options = options

    modalStore.addModal({
      id: this._idModal,
      title: 'Phone number',
      ModalContentProps: {
        store: this,
      },
      ModalActions: ContactChoosePhoneModalActions,
      ModalContent: ContactChoosePhoneModalContent,
      onClose: () => {
        options.onClose?.()
        this.closeModal()
      },
    })
  }

  closeModal = () => {
    this._loading = false
    this._options = null
    this._numbersMap.clear()
    this._activeNumber = ''
    this._activeLabel = ''
    this._activeField = ''
    modalStore.removeModal(this._idModal)
  }

  getNumberItemByType = (type: string) => {
    return this._numbersMap.get(type)
  }

  handleChooseNumber = (type?: string | number) => {
    if (!type) return
    if (typeof type === 'number') return

    const numberItem = this.getNumberItemByType(type)

    this._activeField = numberItem?.label || ''
    this._activeNumber = numberItem?.value || ''
    this._activeLabel = formatPhoneNumberNational(numberItem?.value || '')
    this._activeType = type || ''
  }

  handleSubmit = async () => {
    if (!this._options) return

    try {
      runInAction(() => {
        this._loading = true
      })

      const item = this._options.contact
      const integration = this._integrationsStore.getIntegration(item.integration_type)

      const params: IParamsCreateContact = {
        forLink: true,
        fromConversation: true,
        integration_id: integration?.id,
        ...item.params,
        ...this._options.params,
        phone_type: this._activeType,
        number: this._activeNumber,
      }

      const isContactExist = await this._contactsStore.getContactByNumber(this._activeNumber)

      if (!isContactExist) {
        this.setAlert({
          type: 'success',
          desc: 'Contact created',
        })
      }

      if (this._options.params && !this._options.params.forLink) {
        this._options.callback(item)
        this.closeModal()
        return
      }

      const contact = await this._contactsStore.createContact(params)

      if (contact) {
        this._options.callback(contact)
      }

      this.closeModal()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  get numbers() {
    return Array.from(this._numbersMap.values())
  }

  get disabled() {
    return !this._activeNumber || this._loading
  }

  get alert() {
    return this._alert
  }

  get loading() {
    return this._loading
  }

  get options() {
    return this._options
  }

  get activeLabel() {
    return this._activeLabel
  }

  get activeField() {
    return this._activeField
  }

  get activeType() {
    return this._activeType
  }

  setAlert = (item: IAlert) => {
    this._alert = item
    this._config?.onChangeAlert?.(item)
  }

  resetAlert = () => {
    this._alert = null
    this._config?.onChangeAlert?.(null)
  }
}
